import React from 'react';
import { Car, Fuel, Gauge, Calendar, Cog, MapPin } from 'lucide-react';

const VehiclesDetails = ({ listing }) => {
  const details = listing.specialized_data || {};
  
  if (!details || details._type !== 'vehicles') return null;

  const specs = [
    { icon: Car, label: 'Make', value: details.make },
    { icon: Car, label: 'Model', value: details.model },
    { icon: Calendar, label: 'Year', value: details.year },
    { icon: Fuel, label: 'Fuel Type', value: details.fuel_type },
    { icon: Gauge, label: 'Mileage', value: details.mileage },
    { icon: Cog, label: 'Transmission', value: details.transmission },
  ];

  const features = [
    { label: 'AC', value: details.has_ac },
    { label: 'Power Steering', value: details.power_steering },
    { label: 'Power Windows', value: details.power_windows },
    { label: 'Airbags', value: details.airbags },
    { label: 'ABS', value: details.abs },
    { label: 'Alarm System', value: details.alarm_system },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Vehicle Details</h2>
      
      {/* Basic Specs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        {specs.map((spec, index) => {
          if (!spec.value) return null;
          return (
            <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <spec.icon className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500">{spec.label}</p>
                <p className="font-medium text-gray-900">{spec.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Features */}
      {features.filter(f => f.value).length > 0 && (
        <div className="mb-6">
          <h3 className="font-medium text-gray-900 mb-3">Features</h3>
          <div className="flex flex-wrap gap-2">
            {features.map((feature, index) => {
              if (!feature.value) return null;
              return (
                <span 
                  key={index} 
                  className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-red-100 text-red-800"
                >
                  {feature.label}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Additional Information */}
      {(details.color || details.body_type || details.engine_capacity || details.condition) && (
        <div className="border-t pt-4">
          <h3 className="font-medium text-gray-900 mb-3">Additional Information</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {details.color && (
              <div>
                <p className="text-sm text-gray-500">Color</p>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded-full border" 
                    style={{ backgroundColor: details.color.toLowerCase() }}
                  />
                  <p className="font-medium text-gray-900">{details.color}</p>
                </div>
              </div>
            )}
            {details.body_type && (
              <div>
                <p className="text-sm text-gray-500">Body Type</p>
                <p className="font-medium text-gray-900">{details.body_type}</p>
              </div>
            )}
            {details.engine_capacity && (
              <div>
                <p className="text-sm text-gray-500">Engine Capacity</p>
                <p className="font-medium text-gray-900">{details.engine_capacity}</p>
              </div>
            )}
            {details.condition && (
              <div>
                <p className="text-sm text-gray-500">Condition</p>
                <p className="font-medium text-gray-900 capitalize">{details.condition}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ownership Details */}
      {(details.ownership || details.registration_year || details.insurance_validity) && (
        <div className="border-t pt-4">
          <h3 className="font-medium text-gray-900 mb-3">Ownership Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {details.ownership && (
              <div>
                <p className="text-sm text-gray-500">Ownership</p>
                <p className="font-medium text-gray-900">{details.ownership}</p>
              </div>
            )}
            {details.registration_year && (
              <div>
                <p className="text-sm text-gray-500">Registration Year</p>
                <p className="font-medium text-gray-900">{details.registration_year}</p>
              </div>
            )}
            {details.insurance_validity && (
              <div>
                <p className="text-sm text-gray-500">Insurance Validity</p>
                <p className="font-medium text-gray-900">{details.insurance_validity}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VehiclesDetails;